// Утилиты для фильтрации, сортировки, валидации

import {
  Document,
  DocumentsStats,
  TableRowData,
  DocumentType,
  DocumentStatus,
  DocumentDirection,
  SortBy,
  SortOrder
} from './types';

// Статусы ошибок для функции "Создать на основании"
export const ERROR_STATUSES = [5, 11, 14]; // 5 - Отклонен, 11 - Ошибка остатка, 14 - Ошибка 1С

/**
 * Проверка, является ли статус документа ошибкой
 */
export const isErrorStatus = (status: number): boolean => {
  return ERROR_STATUSES.includes(status);
};

// Статусы для группы "В работе"
export const IN_PROGRESS_STATUSES = [2, 6, 7, 15]; // 2 - Передан, 6 - В пути, 7 - В работе, 15 - Ожидает подтверждения

export const isInProgressStatus = (status: number): boolean => {
  return IN_PROGRESS_STATUSES.includes(status);
};

// Утилиты для работы с документами

/**
 * Получение текста типа документа
 */
export const getDocumentTypeText = (type: number): string => {
  return type === 4 ? 'Возврат' : 'Перемещение между аптеками';
};

/**
 * Получение текста статуса документа
 */
export const getStatusText = (status: number, doc?: any): string => {
  if (status === 6 && doc && doc.typeMovement === 2 && doc.currentPoint) {
    const cp = doc.currentPoint.trim();
    const cpLower = cp.toLowerCase();

    // Проверим, является ли cp логином (не содержит кириллицы)
    const hasCyrillic = /[а-яА-ЯёЁ]/.test(cp);

    if (!hasCyrillic) {
      const creator = (doc.username || '').trim().toLowerCase();
      if (creator && (cpLower === creator || cpLower.includes(creator) || creator.includes(cpLower))) {
        return 'Ожидает отправки';
      }
      return `Ожидает приемки (${cp})`;
    } else {
      let placeName = cp;
      if (!cpLower.includes('склад') && !cpLower.includes('рц') && !cpLower.includes('центр')) {
        placeName = `РЦ ${cp}`;
      } else if (cpLower.includes('рц')) {
        placeName = `РЦ ${cp.replace(/рц/i, '').trim()}`;
      } else if (cpLower.includes('склад')) {
        placeName = `складе ${cp.replace(/склад(ы|а|у|ом|е)?/i, '').trim()}`;
      }
      return `В пути на ${placeName}`;
    }
  }

  switch (status) {
    case 1: return 'Новый';
    case 2: return 'Передан';
    case 3: return 'Принят';
    case 4: return 'Принят частично';
    case 5: return 'Отклонен';
    case 6: return 'В пути';
    case 7: return 'В обработке'; // В работе
    case 9: return 'Черновик';
    case 10: return 'Принят с расхождениями';
    case 11: return 'Ошибка остатка';
    case 12: return 'Сканирование коробов';
    case 13: return 'Подтвердить отправку';
    case 14: return 'Ошибка 1С';
    case 15: return 'Ожидает подтверждения';
    case 16: return 'Завершен';
    default: return 'Неизвестный статус';
  }
};

/**
 * Получение цвета статуса документа
 */
export const getStatusColor = (status: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 1: return 'info';     // Новый
    case 2: return 'primary';  // Передан
    case 3: return 'success';  // Принят
    case 4: return 'success';  // Принят частично
    case 5: return 'error';    // Отклонен
    case 6: return 'secondary'; // В пути
    case 7: return 'info';     // В работе
    case 9: return 'warning';  // Черновик
    case 10: return 'success'; // Принят с расхождениями
    case 11: return 'error';   // Ошибка остатка
    case 12: return 'warning'; // Сканирование коробов
    case 13: return 'warning'; // Подтвердить отправку
    case 14: return 'error'; // Ошибка 1С
    case 15: return 'success'; // Ожидает подтверждения
    case 16: return 'secondary'; // Завершен
    default: return 'default';
  }
};

/**
 * Получение приоритета статуса для сортировки
 * Чем меньше число - тем выше приоритет (документ отображается выше в списке)
 * Отсчет начинается с 1
 */
export const getStatusPriority = (status: number): number => {
  switch (status) {
    case 12: return 1;  // Сканирование коробов (Самый высокий приоритет)
    case 1: return 2;  // Новый
    case 15: return 1; // Ожидает подтверждения
    case 7: return 3;  // В работе
    case 9: return 4;  // Черновик
    case 2: return 5;  // Передан
    case 6: return 6;  // В пути
    case 3: return 7; // Принят
    case 4: return 8;  // Принят частично
    case 10: return 9;  // Принят с расхождениями
    case 16: return 10; // Завершен
    case 11: return 11;  // Ошибка остатка
    case 5: return 12; // Отклонен
    default: return 99; // Неизвестный статус
  }
};

/**
 * Получение названия аптеки
 */
export const getPharmacyName = (pharmacyNum: string): string => {
  return `Аптека №${pharmacyNum}`;
};

/**
 * Получение номера документа для отображения
 */
export const getDocumentNumber = (document: Document): number => {
  // Проверяем, является ли id простым числом
  const simpleNumber = parseInt(document.id, 10);
  if (!isNaN(simpleNumber) && simpleNumber.toString() === document.id.toString()) {
    return simpleNumber;
  }

  // Для сложных id (например, перемещений между аптеками)
  // Ищем последнее число в строке - обычно это номер документа
  const matches = document.id.match(/\d+/g);
  if (matches && matches.length > 0) {
    return parseInt(matches[matches.length - 1], 10);
  }

  // Fallback: пытаемся извлечь любое число из строки
  const fallbackMatch = document.id.match(/(\d+)/);
  return fallbackMatch ? parseInt(fallbackMatch[1], 10) : 0;
};

/**
 * Форматирование даты без изменения часового пояса
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';

  try {
    // Если дата в формате "26.08.2025, 19:03:20", оставляем как есть
    if (dateString.includes('.') && dateString.includes(',')) {
      return dateString;
    }

    let day: number, month: number, year: number, hours: number, minutes: number, seconds: number;

    // Если дата в ISO формате с Z (2025-08-27T12:56:44.410Z)
    if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/)) {
      // Парсим время напрямую из строки без конвертации timezone
      const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        year = parseInt(match[1]);
        month = parseInt(match[2]);
        day = parseInt(match[3]);
        hours = parseInt(match[4]);
        minutes = parseInt(match[5]);
        seconds = parseInt(match[6]);
      } else {
        throw new Error('Не удалось распарсить ISO дату');
      }
    }
    // Если дата из базы (формат SQL)
    else if (dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{3})?$/)) {
      // Парсим время напрямую из строки без конвертации timezone
      const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        year = parseInt(match[1]);
        month = parseInt(match[2]);
        day = parseInt(match[3]);
        hours = parseInt(match[4]);
        minutes = parseInt(match[5]);
        seconds = parseInt(match[6]);
      } else {
        throw new Error('Не удалось распарсить SQL дату');
      }
    }
    // Для всех остальных форматов используем стандартный парсинг
    else {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      day = date.getDate();
      month = date.getMonth() + 1;
      year = date.getFullYear();
      hours = date.getHours();
      minutes = date.getMinutes();
      seconds = date.getSeconds();
    }

    return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}, ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } catch (error) {
    return dateString; // Возвращаем оригинальную строку при ошибке
  }
};

/**
 * Проверка является ли документ новым (< 1 часа И создан НЕ текущим пользователем)
 */
export const isNewDocument = (doc: Document, currentUsername: string): boolean => {
  // Пометка "Новый" только для документов, созданных НЕ владельцем аккаунта
  const isNotMyDocument = doc.username && doc.username !== currentUsername;

  if (!isNotMyDocument) {
    return false; // Если это мой документ - не показываем "Новый"
  }

  const now = new Date().getTime();

  // Используем ISO формат для корректного парсинга
  let dateToCheck;

  if (doc.updateDateISO) {
    dateToCheck = new Date(doc.updateDateISO).getTime();
  } else if (doc.createdAtISO) {
    dateToCheck = new Date(doc.createdAtISO).getTime();
  } else {
    // Fallback к старому методу если ISO форматы недоступны
    dateToCheck = doc.updateDate
      ? new Date(doc.updateDate).getTime()
      : new Date(doc.createdAt).getTime();
  }

  const oneHourAgo = now - (60 * 60 * 1000); // 1 час в миллисекундах
  return dateToCheck > oneHourAgo;
};

/**
 * Валидация формы документа
 */
export const isFormValid = (
  documentType: number,
  toPharmacy: any,
  tableData: TableRowData[],
  toStorage?: any
): boolean => {
  if (documentType === 5 && !toPharmacy) return false;
  if (documentType === 4 && !toStorage) return false;
  if (tableData.length === 0) return false;

  return tableData.every(row => {
    const hasQuantity = row.Quantity && parseFloat(row.Quantity) > 0;
    const hasQuantityPcs = row.QuantityPcs && parseFloat(row.QuantityPcs) > 0;
    // Для документов типа 4 (Возврат/склад) нужно заполнить серии по кол-ву товара
    let hasSeriesIfRequired = true;
    if (documentType === 4) {
      const qty = Math.ceil(parseFloat(row.Quantity || '0') || 0);
      const seriesArr: any[] = row.series
        ? (() => { try { return JSON.parse(row.series); } catch { return []; } })()
        : [];
      const filled = seriesArr.filter(s => {
        const name = typeof s === 'string' ? s : (s?.name || '');
        return name && name.trim().length > 0;
      }).length;
      hasSeriesIfRequired = qty > 0 && filled === qty;
    }
    return (
      row.selectedMaterial && // Товар должен быть выбран из списка
      row.MaterialCode &&
      row.MaterialName &&
      (hasQuantity || hasQuantityPcs) && // Хотя бы одно из полей должно быть заполнено
      hasSeriesIfRequired
    );
  });
};

/**
 * Подсчет необходимого количества маркировочных кодов
 * Формула: Quantity (упаковки) + ceil(QuantityPcs / PartCoeff)
 * 
 * Примеры с PartCoeff = 30:
 * - 1 упак, 0 шт → 1 код
 * - 1 упак, 20 шт → 1 + ceil(20/30) = 1 + 1 = 2 кода
 * - 1 упак, 31 шт → 1 + ceil(31/30) = 1 + 2 = 3 кода
 */
export const calculateMarkCodesCount = (
  quantity: string,
  quantityPcs?: string,
  partCoeff?: number
): number => {
  const qty = safeParseNumber(quantity);
  const pcs = safeParseNumber(quantityPcs || '0');
  const coeff = partCoeff || 0;

  if (qty <= 0 && pcs <= 0) return 0;

  let count = Math.ceil(qty);

  if (pcs > 0 && coeff > 0) {
    count += Math.ceil(pcs / coeff);
  }

  return count;
};

const MAX_QUANTITY = 256;

function safeParseNumber(value: string): number {
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num < 0) return 0;
  if (num > MAX_QUANTITY) {
    console.error('СЛИШКОМ БОЛЬШОЕ значение quantity/quantityPcs:', value);
    return 0;
  }
  return num;
}

/**
 * Создание массива маркировочных кодов нужного размера
 */
export const createMarkCodesArray = (
  quantity: string,
  existingCodes: string[] = [],
  quantityPcs?: string,
  partCoeff?: number
): string[] => {
  const rawCount = calculateMarkCodesCount(quantity, quantityPcs, partCoeff);
  const neededCount = Number.isFinite(rawCount) && rawCount >= 0
    ? Math.min(Math.floor(rawCount), 100000)
    : 0;

  const codes = [...existingCodes];

  while (codes.length < neededCount) {
    codes.push('');
  }

  if (codes.length > neededCount) {
    codes.splice(neededCount);
  }

  return codes;
};

/**
 * Создание пустой строки таблицы
 */
export const createEmptyRow = (): TableRowData => ({
  id: Date.now().toString() + Math.random(),
  MaterialZid: '',
  MaterialCode: '',
  MaterialName: '',
  SerialNumber: '',
  SerialDate: '',
  Quantity: '',
  Quantity1C: '',
  Barcode: '',
  Comment: '',
  selectedMaterial: undefined,
  markCodes: []
});

/**
 * Получение статистики документов
 */
export const getDocumentsStats = (
  documents: Document[],
  incomingDocuments: Document[]
): DocumentsStats => {
  const allDocuments = [...documents, ...incomingDocuments];

  return {
    total: allDocuments.length,
    drafts: documents.filter(doc => doc.status === 9).length, // Черновики только у исходящих (статус 9)
    inProgress: allDocuments.filter(doc => IN_PROGRESS_STATUSES.includes(doc.status || 0)).length,
    transferred: allDocuments.filter(doc => doc.status === 2).length, // Передан (статус 2)
    accepted: allDocuments.filter(doc => doc.status === 3).length, // Принят (статус 3)
    partiallyAccepted: allDocuments.filter(doc => doc.status === 4).length, // Принят частично (статус 4)
    rejected: allDocuments.filter(doc => doc.status === 5).length, // Отклонен (статус 5)
    returns: allDocuments.filter(doc => doc.type === 4).length,
    transfers: allDocuments.filter(doc => doc.type === 5).length,
    outgoing: documents.length,
    incoming: incomingDocuments.length,
    totalErrors: allDocuments.filter(doc => ERROR_STATUSES.includes(doc.status || 0)).length,
  };
};

/**
 * Фильтрация и сортировка документов
 */
export const getFilteredAndSortedDocuments = (
  documents: Document[],
  incomingDocuments: Document[],
  filterType: DocumentType,
  filterStatus: DocumentStatus,
  filterDirection: DocumentDirection,
  sortBy: SortBy,
  sortOrder: SortOrder,
  currentUsername: string
): Document[] => {
  const documentsMap = new Map<string | number, Document>();

  documents.forEach(doc => {
    documentsMap.set(doc.id, doc);
  });

  incomingDocuments.forEach(doc => {
    documentsMap.set(doc.id, doc);
  });

  let allDocuments = Array.from(documentsMap.values());

  let filtered = allDocuments.filter(doc => {
    // Фильтр по типу документа
    if (filterType !== 'all' && doc.type !== filterType) {
      return false;
    }

    // Фильтр по статусу
    if (filterStatus !== 'all') {
      if (filterStatus === 'error') {
        if (!isErrorStatus(doc.status || 0)) return false;
      } else if (filterStatus === 'in_progress') {
        if (!isInProgressStatus(doc.status || 0)) return false;
      } else if (doc.status !== filterStatus) {
        return false;
      }
    }

    // Фильтр по направлению
    if (filterDirection !== 'all') {
      if (filterDirection === 'outgoing' && doc.isIncoming) {
        return false;
      }
      if (filterDirection === 'incoming' && !doc.isIncoming) {
        return false;
      }
    }

    return true;
  });

  // Разделяем на новые и обычные документы
  const newDocs = filtered.filter(doc => isNewDocument(doc, currentUsername));
  const regularDocs = filtered.filter(doc => !isNewDocument(doc, currentUsername));

  // Функция сортировки
  const sortDocuments = (docs: Document[]) => {
    return docs.sort((a, b) => {
      // СНАЧАЛА сортируем по приоритету статуса (самый важный критерий)
      const priorityA = getStatusPriority(a.status || 0);
      const priorityB = getStatusPriority(b.status || 0);

      if (priorityA !== priorityB) {
        return priorityA - priorityB; // Чем меньше приоритет - тем выше в списке
      }

      // Если приоритеты равны - сортируем по выбранному критерию
      let compareValue = 0;

      switch (sortBy) {
        case 'id':
          compareValue = parseInt(a.id) - parseInt(b.id);
          break;
        case 'date':
          // Используем ISO формат для корректной сортировки по дате
          const dateA = a.createdAtISO ? new Date(a.createdAtISO).getTime() : new Date(a.createdAt).getTime();
          const dateB = b.createdAtISO ? new Date(b.createdAtISO).getTime() : new Date(b.createdAt).getTime();
          compareValue = dateA - dateB;
          break;
        case 'type':
          compareValue = a.type - b.type;
          break;
        case 'status':
          compareValue = (a.status || 0) - (b.status || 0);
          break;
        default:
          compareValue = 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  };

  // Сортируем новые и обычные документы отдельно
  const sortedNewDocs = sortDocuments(newDocs);
  const sortedRegularDocs = sortDocuments(regularDocs);

  // Объединяем: новые документы всегда вверху
  return [...sortedNewDocs, ...sortedRegularDocs];
};

/**
 * Получение всех отфильтрованных документов (без пагинации)
 * Для использования с виртуализированным списком
 */
export const getAllFilteredDocuments = (
  documents: Document[],
  incomingDocuments: Document[],
  filterType: DocumentType,
  filterStatus: DocumentStatus,
  filterDirection: DocumentDirection,
  sortBy: SortBy,
  sortOrder: SortOrder,
  currentUsername: string
): Document[] => {
  return getFilteredAndSortedDocuments(
    documents,
    incomingDocuments,
    filterType,
    filterStatus,
    filterDirection,
    sortBy,
    sortOrder,
    currentUsername
  );
};
